/* eslint-disable */
/**
 * InvoiceAgentTab — Agent Faktur v2 (Vendor Group Architecture).
 *
 * Filozofia: 1 kontrahent (NIP) = 1 grupa faktur = 1 prompt = 1 profil.
 * Wzorzec UI skopiowany 1:1 z `BankAgentTab.jsx`. Różnice domenowe:
 *  - klucz to `vendor_nip` (NIP sprzedawcy), nie `counterparty_key`,
 *  - dane źródłowe: zaimportowane faktury PAX (kolekcja `invoices`)
 *    oraz `ksef_invoices` (jeszcze nie zaimportowane),
 *  - metoda płatności pochodzi z Agenta Wyciągów (read-only badge).
 *
 * Endpointy backendowe:
 *  - GET  /api/ai/agents/invoice/vendors
 *  - POST /api/ai/agents/invoice/analyze-vendors
 *  - GET  /api/ai/agents/invoice/vendors/{vendor_nip}/invoices
 *  - POST /api/ai/agents/invoice/analyze-vendor (reanaliza pojedynczego)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Sparkles, RefreshCw, CheckCircle2, AlertCircle, Clock, Inbox,
  ChevronDown, ChevronRight, XCircle, Banknote,
  BadgeCheck, CircleSlash, Search, X as XIcon, ArrowUpAZ, ArrowDownAZ,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useInvoiceAutoAnalysisJob } from "@/hooks/useInvoiceAutoAnalysisJob";
import { useInvoicePreview } from "@/contexts/InvoicePreviewContext";
import AgentDebugPanel from "@/components/agent-debug/AgentDebugPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const METHOD_LABEL = {
  transfer: "Przelew", card: "Karta", blik: "BLIK", cash: "Gotówka",
  online: "Płatność online", online_payment: "Płatność online",
  other_non_transfer: "Inna forma (nie-przelew)",
  mixed: "Mieszane", unknown: "Nieznana",
};

const STATUS_META = {
  not_analyzed: { label: "Do analizy", icon: Clock, cls: "bg-gray-100 text-gray-700 border-gray-300" },
  analyzing: { label: "Analizuję", icon: Loader2, cls: "bg-blue-50 text-blue-700 border-blue-300 animate-pulse", spin: true },
  analyzed: { label: "Przeanalizowany", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  cached: { label: "Cache", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  fresh: { label: "Przeanalizowany", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  reanalyzed: { label: "Odświeżony", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  needs_review: { label: "Wymaga uwagi", icon: AlertCircle, cls: "bg-orange-50 text-orange-700 border-orange-300" },
  failed: { label: "Błąd", icon: XCircle, cls: "bg-red-50 text-red-700 border-red-300" },
};

const formatPLN = (v) =>
  `${Number(v || 0).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} PLN`;

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.not_analyzed;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${m.cls}`}>
      <Icon className={`w-3 h-3 ${m.spin ? "animate-spin" : ""}`} />{m.label}
    </span>
  );
};

/**
 * Metody płatności, dla których profil bankowy w wyciągach jest **nieaplikowalny**.
 *
 * Reguła biznesowa (2026-07): jeśli Agent Faktur jednoznacznie ustalił metodę
 * płatności jako inną niż przelew, ta faktura z definicji nie powinna mieć
 * dopasowania w `payment_counterparty_profiles`. Brak profilu bankowego NIE
 * jest wtedy błędem — jest stanem oczekiwanym.
 *
 * Enum zgodny z `services/vendor_payment_resolver.py::_ALLOWED_METHODS`
 * (backend). `online` = kategoria AutoPay / eBOK / paneli operatorów
 * (2026-02, patrz `routers/vendors.py:52`).
 *
 * Świadomie NIE zawiera:
 *   - `transfer` — nadal wymaga profilu bankowego (obecna logika),
 *   - `mixed` — mieszany, może wymagać profilu,
 *   - `unknown` — nieustalony, komunikat „Brak danych w wyciągach" jest
 *     wtedy poprawną informacją dla usera (brak decyzji = brak wykluczenia).
 */
const NON_TRANSFER_METHODS = new Set(["cash", "card", "blik", "online"]);

/**
 * BankConfirmationBadge — informacja "czy kontrahent jest potwierdzony
 * w Agencie Wyciągów" widoczna od razu, bez rozwijania wiersza.
 *
 * Źródło danych: backend `list_vendors` (services/ai_invoice_profile.py)
 * już dołącza `has_bank_profile` (boolean) na podstawie istniejącego joina
 * z `payment_counterparty_profiles` po znormalizowanej nazwie sprzedawcy.
 * NIE używamy nowego endpointu / nowej kolekcji — wykorzystujemy istniejący
 * mechanizm powiązania (analog `bank.payment_method` w panelu rozwiniętym).
 *
 * Warunkowe wyciszenie (2026-07): dla metod z `NON_TRANSFER_METHODS` badge
 * pokazuje neutralny komunikat „Nie dotyczy (płatność poza przelewem)"
 * niezależnie od wartości `hasBankProfile`. Reguła dotyczy WYŁĄCZNIE tych
 * metod — dla przelewu logika bez zmian.
 */
const BankConfirmationBadge = ({ hasBankProfile, paymentMethod, idx }) => {
  if (paymentMethod && NON_TRANSFER_METHODS.has(paymentMethod)) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-stone-50 text-stone-600 border-stone-200"
        data-testid={`invoice-agent-vendor-bank-badge-${idx}`}
        title="Metoda płatności inna niż przelew — dopasowanie w wyciągach nie jest wymagane"
      >
        <CircleSlash className="w-3 h-3" />
        Nie dotyczy (płatność poza przelewem)
      </span>
    );
  }
  if (hasBankProfile) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-blue-50 text-blue-700 border-blue-300"
        data-testid={`invoice-agent-vendor-bank-badge-${idx}`}
        title="Kontrahent ma profil płatniczy w Agencie Wyciągów"
      >
        <BadgeCheck className="w-3 h-3" />
        Potwierdzony w wyciągach
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-gray-100 text-gray-600 border-gray-300"
      data-testid={`invoice-agent-vendor-bank-badge-${idx}`}
      title="Brak profilu płatniczego w Agencie Wyciągów"
    >
      <CircleSlash className="w-3 h-3" />
      Brak danych w wyciągach
    </span>
  );
};

// Szczegóły faktur per kontrahent (expandable row content)
// Pakiet KSEF-ID (2026-02-24): jeden wiersz per `ksef_number`. Badge PAX+KSeF
// jako informacja o technicznych źródłach, numer faktury klikalny — otwiera
// istniejący globalny podgląd (useInvoicePreview) używany też przez Faktury
// i KSeF Inbox.
const VendorInvoices = ({ vendorNip }) => {
  const [invoices, setInvoices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { openInvoice } = useInvoicePreview();

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/ai/agents/invoice/vendors/${encodeURIComponent(vendorNip)}/invoices`,
          { withCredentials: true }
        );
        if (alive) setInvoices(r.data?.invoices || []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Błąd pobierania faktur.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [vendorNip]);

  if (loading) {
    return (
      <div className="text-xs text-gray-500 px-3 py-2">
        <Loader2 className="w-3 h-3 inline animate-spin mr-1.5" />Ładowanie faktur…
      </div>
    );
  }
  if (error) return <div className="text-xs text-red-600 px-3 py-2">{error}</div>;
  if (!invoices?.length) return <div className="text-xs text-gray-500 px-3 py-2">Brak faktur.</div>;

  const renderSources = (inv) => {
    // Backend zwraca listę `sources` (["invoices"], ["ksef_invoices"] lub oba).
    // Fallback: `source` legacy field.
    const sources = Array.isArray(inv.sources) && inv.sources.length
      ? inv.sources
      : [inv.source || "invoices"];
    const badges = [];
    if (sources.includes("invoices")) {
      badges.push(
        <span
          key="pax"
          className="text-xs px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200"
          data-testid={`invoice-agent-source-badge-pax-${inv.invoice_id}`}
        >
          PAX
        </span>
      );
    }
    if (sources.includes("ksef_invoices")) {
      badges.push(
        <span
          key="ksef"
          className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
          data-testid={`invoice-agent-source-badge-ksef-${inv.invoice_id}`}
        >
          KSeF
        </span>
      );
    }
    return badges;
  };

  return (
    <div
      className="px-3 py-2 border-t border-gray-100 bg-gray-50/60"
      data-testid={`invoice-agent-vendor-invoices-${vendorNip}`}
    >
      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1.5">
        Faktury źródłowe ({invoices.length})
      </div>
      <div className="space-y-1">
        {invoices.map((inv) => (
          <div
            key={inv.invoice_id || inv.ksef_number}
            className="grid grid-cols-12 gap-2 text-xs py-1.5 border-b border-gray-100 last:border-b-0 items-center"
            data-testid={`invoice-agent-vendor-invoice-${inv.invoice_id || inv.ksef_number}`}
          >
            <div className="col-span-2 text-gray-600 tabular-nums">
              {inv.issue_date?.slice(0, 10) || "—"}
            </div>
            <div className="col-span-3 break-all">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openInvoice(inv);
                }}
                className="text-blue-700 font-medium hover:underline hover:text-blue-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded-sm text-left break-all"
                title={inv.invoice_number || inv.ksef_number || "Otwórz podgląd"}
                data-testid={`invoice-agent-vendor-invoice-preview-${inv.invoice_id || inv.ksef_number}`}
              >
                {inv.invoice_number || inv.ksef_number || "—"}
              </button>
            </div>
            <div className="col-span-2 text-right font-medium tabular-nums text-gray-900">
              {formatPLN(inv.gross_amount)}
            </div>
            <div className="col-span-2 flex flex-wrap gap-1">
              {renderSources(inv)}
            </div>
            <div
              className="col-span-3 text-gray-500 font-mono text-xs break-all"
              title={inv.bank_account || ""}
            >
              {inv.bank_account || "—"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * DataReadinessPanel — informacyjny wskaźnik gotowości danych do analizy.
 *
 * NIE blokuje analizy, NIE wyłącza przycisków, NIE wymusza oczekiwania.
 * Otrzymuje z backendu jedynie abstrakcyjną parę `{ready, total}` i zamienia
 * ją na kolor + komunikat zgodnie z wymaganiami UX. Frontend nie zna pojęć
 * KSeF/XML — to celowa abstrakcja.
 *
 * Stany kolorystyczne:
 *   ready == total > 0 → zielony (wszystko gotowe)
 *   ready == 0,  total > 0 → czerwony (nic jeszcze nie gotowe)
 *   0 < ready < total → żółty (trwa pobieranie)
 *   total == 0 → panel ukryty (brak danych do raportowania)
 */
const DataReadinessPanel = ({ readiness }) => {
  if (!readiness) return null;
  const { ready, total } = readiness;
  if (total <= 0) return null;

  let tone;
  let title;
  let footer;
  if (ready >= total) {
    tone = "green";
    title = "Wszystkie dane są gotowe do analizy.";
    footer = "Możesz rozpocząć analizę.";
  } else if (ready <= 0) {
    tone = "red";
    title = "Dane nie są jeszcze gotowe do analizy.";
    footer = "Poczekaj na pobranie wszystkich danych.";
  } else {
    tone = "yellow";
    title = "Nie wszystkie dane są jeszcze gotowe.";
    footer = "Dla najlepszych wyników poczekaj na pobranie wszystkich danych.";
  }

  const palette = {
    green: {
      wrap: "bg-emerald-50 border-emerald-200 text-emerald-900",
      dot: "bg-emerald-500",
      count: "text-emerald-900",
    },
    yellow: {
      wrap: "bg-amber-50 border-amber-200 text-amber-900",
      dot: "bg-amber-500",
      count: "text-amber-900",
    },
    red: {
      wrap: "bg-rose-50 border-rose-200 text-rose-900",
      dot: "bg-rose-500",
      count: "text-rose-900",
    },
  }[tone];

  return (
    <div
      className={`rounded-lg border px-4 py-3 ${palette.wrap}`}
      data-testid="invoice-agent-readiness-panel"
      data-readiness-tone={tone}
    >
      <div className="flex items-start gap-2.5">
        <span className={`mt-1.5 inline-block w-2.5 h-2.5 rounded-full shrink-0 ${palette.dot}`} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold" data-testid="invoice-agent-readiness-title">
            {title}
          </div>
          <div className={`text-xs mt-0.5 font-mono ${palette.count}`} data-testid="invoice-agent-readiness-count">
            {ready} / {total} faktur gotowych do pełnej analizy.
          </div>
          <div className="text-xs mt-1 opacity-90" data-testid="invoice-agent-readiness-footer">
            {footer}
          </div>
        </div>
      </div>
    </div>
  );
};

const InvoiceAgentTab = () => {
  const [vendors, setVendors] = useState([]);
  const [readiness, setReadiness] = useState(null);  // {ready, total} | null
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());   // vendor_nips
  const [busyKeys, setBusyKeys] = useState(() => new Set());   // per-card "Sprawdź ponownie"
  const [lastDebugInfo, setLastDebugInfo] = useState(null);
  // Setting persistowany w `/api/agent-settings` (round unify 2026-02).
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoBusy, setAutoBusy] = useState(true);
  // Wyszukiwarka w stylu bankowym: dropdown sugestii po ≥3 znakach.
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  // Sortowanie po nazwie kontrahenta (asc = A-Z, desc = Z-A).
  const [sortDir, setSortDir] = useState("asc");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/ai/agents/invoice/vendors`,
        { withCredentials: true }
      );
      setVendors(res.data?.vendors || []);
      const r = res.data?.readiness;
      // Backend gwarantuje shape {ready, total}; defensywny guard na wypadek
      // starszej odpowiedzi (rolling deploy).
      if (r && typeof r.ready === "number" && typeof r.total === "number") {
        setReadiness({ ready: r.ready, total: r.total });
      } else {
        setReadiness(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udało się pobrać kontrahentów.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /**
   * `visibleVendors` — jedno źródło prawdy dla listy kontrahentów widocznych
   * w UI Agenta Faktur.
   *
   * Wizualny guardrail nad backendowym `workflow_status` (Source of Truth,
   * services/vendor_workflow_status.py). Kontrahenci rozliczani inną metodą
   * niż przelew otrzymują `workflow_status === "excluded_by_payment_method"`
   * i NIE powinni pojawiać się w żadnym widoku Agenta Faktur — ani na liście
   * kontrahentów, ani w licznikach, ani w zaznaczeniach zbiorczych, ani w
   * auto-runnerach. Filtrujemy tu raz, po pobraniu danych; cała dalsza
   * logika (sortowanie, filtry UI, statystyki, batch, auto) bazuje na tej
   * przefiltrowanej tablicy.
   *
   * NIE modyfikujemy backendu — endpoint `/api/ai/agents/invoice/vendors`
   * zwraca pełną listę (potrzebną m.in. dla /api/vendors i innych paneli).
   * Filtr jest wyłącznie warstwą prezentacji Agenta Faktur.
   */
  const visibleVendors = useMemo(
    () => (vendors || []).filter(
      (v) => v?.workflow_status !== "excluded_by_payment_method"
    ),
    [vendors],
  );

  // Backend-driven auto-analysis job (2026-02-17, ujednolicone 2026-02).
  // Analiza działa po stronie backendu nawet gdy user zamknie stronę.
  // Frontend tylko start/cancel + polling. Trigger = checkbox
  // „Automatycznie analizuj nowych kontrahentów".
  const autoJob = useInvoiceAutoAnalysisJob({
    onComplete: (final) => {
      const skipped = final.skipped ?? 0;
      const base = `Analiza zakończona: ${final.analyzed} zaktualizowano, ${final.cached} z cache`;
      const withSkipped = skipped > 0 ? `${base}, ${skipped} pominięto` : base;
      const msg = `${withSkipped}, ${final.failed} błędów`;
      if (final.status === "completed") {
        if (final.failed > 0) toast.warning(msg);
        else toast.success(msg);
      } else if (final.status === "cancelled") {
        toast.info(`Analiza anulowana po ${final.processed}/${final.total}`);
      } else if (final.status === "failed") {
        toast.error(msg);
      }
      fetchData();
    },
  });

  const unanalyzedCount = visibleVendors.filter(
    (v) => v.profile_status === "not_analyzed"
  ).length;

  // Fetch aktualnego stanu checkboxa z backendu (setting persistowany).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/agent-settings`, { withCredentials: true });
        if (!alive) return;
        setAutoEnabled(!!(r.data?.invoice_agent?.automatically_analyze_new_contractors));
      } catch (_e) {
        if (alive) setAutoEnabled(false);
      } finally {
        if (alive) setAutoBusy(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleToggleAuto = useCallback(async (checked) => {
    setAutoBusy(true);
    setAutoEnabled(!!checked);  // optimistic
    try {
      await axios.put(
        `${API}/agent-settings`,
        { invoice_agent: { automatically_analyze_new_contractors: !!checked } },
        { withCredentials: true },
      );
      if (checked) {
        // Po włączeniu — od razu spróbuj przeanalizować nieprzeanalizowanych.
        try { await autoJob.start(); } catch (_e) { /* silent */ }
      } else if (autoJob.running) {
        // Po wyłączeniu — anuluj aktywną analizę (jeśli trwa).
        try { await autoJob.cancel(); } catch (_e) { /* silent */ }
      }
    } catch (_e) {
      setAutoEnabled(!checked);
      toast.error("Nie udało się zapisać ustawienia.");
    } finally {
      setAutoBusy(false);
    }
  }, [autoJob]);

  const toggleExpand = (nip) => {
    setExpanded((s) => {
      const ns = new Set(s);
      if (ns.has(nip)) ns.delete(nip); else ns.add(nip);
      return ns;
    });
  };

  // Wyszukiwarka + sortowanie A-Z / Z-A po nazwie kontrahenta.
  const searchQ = searchTerm.trim().toLowerCase();
  const nameOf = (v) => (v.vendor_name || "").toLowerCase();
  const items = [...visibleVendors]
    .filter((v) => (searchQ ? (nameOf(v).includes(searchQ) || (v.vendor_nip || "").includes(searchQ)) : true))
    .sort((a, b) => {
      const cmp = (a.vendor_name || "").localeCompare(
        b.vendor_name || "", "pl", { sensitivity: "base" }
      );
      return sortDir === "asc" ? cmp : -cmp;
    });

  // Sugestie: top 5 pasujących kontrahentów (uporządkowane A-Z niezależnie
  // od `sortDir`, żeby lista sugestii była przewidywalna).
  const suggestions = searchQ.length >= 3
    ? [...visibleVendors]
        .filter((v) => nameOf(v).includes(searchQ) || (v.vendor_nip || "").includes(searchQ))
        .sort((a, b) => (a.vendor_name || "").localeCompare(
          b.vendor_name || "", "pl", { sensitivity: "base" }
        ))
        .slice(0, 5)
    : [];

  const computeRowStatus = (v) => {
    const n = v.vendor_nip;
    if (busyKeys.has(n)) return "analyzing";
    return v.profile_status;
  };

  const stats = {
    total: visibleVendors.length,
    analyzed: visibleVendors.filter((v) => v.profile_status === "analyzed").length,
    review: visibleVendors.filter((v) => v.profile_status === "needs_review").length,
    withBank: visibleVendors.filter((v) => v.has_bank_profile).length,
  };

  if (loading) {
    return (
      <div className="text-sm text-gray-500" data-testid="invoice-agent-loading">
        <Loader2 className="w-4 h-4 inline animate-spin mr-2" />Ładowanie kontrahentów…
      </div>
    );
  }
  if (!visibleVendors.length) {
    return (
      <Card
        className="border-dashed border-gray-300 bg-gray-50"
        data-testid="invoice-agent-empty"
      >
        <CardContent className="p-6 flex items-start gap-3">
          <Inbox className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-gray-800">Brak kontrahentów do analizy.</p>
            <p className="text-gray-600 text-xs mt-0.5">
              Lista jest budowana z zaimportowanych faktur (kolekcja Faktury) oraz z KSeF Inboxa.
              Wgraj faktury XML lub pobierz z KSeF, aby aktywować Agenta.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="invoice-agent-tab">
      {/* Status header */}
      <Card className="border border-gray-200">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-gray-900">Kontrahenci do analizy</h3>
          <p className="text-xs text-gray-500 mt-1">
            Agent przetwarza kontrahentów <strong>po kolei</strong> — każdy NIP dostaje
            osobny prompt i zapisywany jest jako osobny profil. Metoda płatności pochodzi
            z Agenta Wyciągów (jeśli dostępna).
          </p>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
            <div>
              <dt className="text-gray-500">Łącznie</dt>
              <dd className="font-semibold text-gray-900" data-testid="invoice-agent-stat-total">{stats.total}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Przeanalizowani</dt>
              <dd className="font-semibold text-emerald-700" data-testid="invoice-agent-stat-analyzed">{stats.analyzed}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Wymagają uwagi</dt>
              <dd className="font-semibold text-orange-700" data-testid="invoice-agent-stat-review">{stats.review}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Z profilem bank.</dt>
              <dd className="font-semibold text-indigo-700" data-testid="invoice-agent-stat-bank">{stats.withBank}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Wskaźnik gotowości danych — wyłącznie informacyjny, nie blokuje analizy. */}
      <DataReadinessPanel readiness={readiness} />

      {/* Karta „Analiza AI Faktur" — zawsze widoczna (standard PAX Agent UX).
          Sekcja progress renderuje się warunkowo w środku. */}
      <Card
        className="border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-violet-50/30"
        data-testid="invoice-agent-analysis-card"
      >
        <CardContent className="p-4 sm:p-5 space-y-3">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-lg bg-white shadow-sm border border-indigo-100 shrink-0">
              <Sparkles className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base sm:text-lg text-gray-900">
                Analiza AI Faktur
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                Buduj profile kontrahentów na podstawie faktur źródłowych z KSeF.
              </p>
            </div>
            {autoJob.running && (
              <Button
                size="sm"
                variant="outline"
                onClick={autoJob.cancel}
                disabled={autoJob.cancelRequested}
                data-testid="invoice-agent-auto-stop"
                className="border-rose-300 text-rose-700 hover:bg-rose-50 shrink-0 disabled:opacity-70"
              >
                {autoJob.cancelRequested
                  ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <XCircle className="w-3.5 h-3.5 mr-1.5" />}
                {autoJob.cancelRequested ? "Anulowanie…" : "Anuluj analizę"}
              </Button>
            )}
          </div>

          {/* Checkbox: „Automatycznie analizuj nowych kontrahentów" —
              JEDYNY sposób uruchamiania analizy (2026-02 unify).
              Setting persistowany w /api/agent-settings. */}
          <div
            className="flex items-center gap-2 text-xs sm:text-sm text-gray-700"
            data-testid="invoice-agent-autoanalyze-toggle-wrap"
          >
            <input
              type="checkbox"
              id="invoice-agent-autoanalyze-toggle"
              data-testid="invoice-agent-autoanalyze-toggle"
              checked={!!autoEnabled}
              disabled={!!autoBusy}
              onChange={(e) => handleToggleAuto(e.target.checked)}
              className="cursor-pointer disabled:opacity-50 disabled:cursor-wait"
            />
            <label
              htmlFor="invoice-agent-autoanalyze-toggle"
              className="cursor-pointer select-none"
            >
              Automatycznie analizuj nowych kontrahentów
            </label>
            {autoBusy ? <Loader2 className="w-3 h-3 animate-spin text-gray-400" /> : null}
            {!autoBusy && !autoJob.running && unanalyzedCount > 0 ? (
              <span className="text-gray-500 text-xs ml-1">
                (do analizy: <strong className="text-gray-700">{unanalyzedCount}</strong>)
              </span>
            ) : null}
          </div>

          {autoJob.running && (
            <>
              {/* Header progresu + licznik */}
              <div className="flex items-center justify-between text-xs text-gray-600 gap-3">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                  <span className="shrink-0">Analizowanie kontrahenta</span>
                </div>
                <span
                  className="font-mono tabular-nums shrink-0"
                  data-testid="invoice-agent-analyze-progress-counter"
                >
                  {autoJob.progress.current} / {autoJob.progress.total}
                </span>
              </div>

              {/* Liniowy pasek postępu */}
              <div
                className="h-1.5 bg-indigo-100 rounded overflow-hidden"
                data-testid="invoice-agent-analyze-progress-bar"
              >
                <div
                  className="h-full bg-indigo-600 transition-all duration-300 ease-out"
                  style={{
                    width: `${
                      autoJob.progress.total > 0
                        ? Math.max(0, Math.min(100, (autoJob.progress.current / autoJob.progress.total) * 100))
                        : 0
                    }%`,
                  }}
                />
              </div>

              {/* Aktualny kontrahent — PEŁNA nazwa bez truncate, na dowolnej
                  liczbie linii; NIP osobno w linii poniżej. Zgodne z zasadą
                  „czytelność ważniejsza niż równa wysokość kart". */}
              {(autoJob.currentVendorNip || autoJob.currentVendorName) && (
                <div
                  className="text-xs sm:text-sm text-gray-800 space-y-0.5"
                  data-testid="invoice-agent-analyze-current-item"
                >
                  <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">
                    Aktualnie analizowany kontrahent
                  </div>
                  <div className="font-semibold break-words whitespace-normal">
                    {autoJob.currentVendorName || "—"}
                  </div>
                  {autoJob.currentVendorNip && (
                    <div className="text-xs text-gray-600 font-mono">
                      NIP: {autoJob.currentVendorNip}
                    </div>
                  )}
                </div>
              )}

              {/* Statystyki inline */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <span className="text-emerald-700">
                  ✓ Zaktualizowane: <strong data-testid="invoice-agent-analyze-analyzed">{autoJob.analyzed}</strong>
                </span>
                <span className="text-emerald-700">
                  ↻ Z cache: <strong data-testid="invoice-agent-analyze-cached">{autoJob.cached}</strong>
                </span>
                {autoJob.skipped > 0 && (
                  <span className="text-slate-600" data-testid="invoice-agent-analyze-skipped-wrapper">
                    ⊘ Pominięte: <strong data-testid="invoice-agent-analyze-skipped">{autoJob.skipped}</strong>
                  </span>
                )}
                <span className="text-red-700">
                  ✗ Błędy: <strong data-testid="invoice-agent-analyze-failed">{autoJob.failed}</strong>
                </span>
              </div>

              {/* Info-banner (niebieski) — standard PAX */}
              <div
                className="rounded border border-indigo-200 bg-indigo-50 text-indigo-900 text-xs p-3 flex items-start gap-2"
                data-testid="invoice-agent-analyze-info-banner"
              >
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-indigo-600" />
                <div>
                  <div className="font-semibold">Analiza działa w tle.</div>
                  <div>Możesz zamknąć przeglądarkę. Postęp zostanie zapisany.</div>
                </div>
              </div>

              {/* Ostatnia aktualizacja */}
              {autoJob.lastUpdatedAt && (
                <div className="text-xs text-gray-500" data-testid="invoice-agent-analyze-last-updated">
                  Ostatnia aktualizacja: {new Date(autoJob.lastUpdatedAt).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}
                </div>
              )}

              {/* Banner anulowania (rose, zgodnie ze standardem) */}
              {autoJob.cancelRequested && (
                <div
                  className="rounded border border-rose-200 bg-rose-50 text-rose-900 text-xs px-2 py-1"
                  data-testid="invoice-agent-analyze-cancel-banner"
                >
                  Zatrzymywanie po bieżącym kontrahencie…
                </div>
              )}

              {/* Ostatni błąd inline */}
              {autoJob.lastError && (
                <div className="text-xs text-red-700 break-words" data-testid="invoice-agent-analyze-last-error">
                  Ostatni błąd:{" "}
                  <strong className="font-mono">{autoJob.lastError.vendor_nip}</strong>
                  {" — "}
                  <span className="break-words">{autoJob.lastError.error}</span>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Toolbar poniżej „Analiza AI Faktur": wyszukiwarka (banking-style
          autocomplete po ≥3 znakach) + sortowanie A-Z / Z-A po nazwie
          kontrahenta. */}
      <Card className="border border-gray-200" data-testid="invoice-agent-search-sort">
        <CardContent className="p-3">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative flex-1 min-w-0" data-testid="invoice-agent-search-wrapper">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setSuggestionsOpen(true); }}
                onFocus={() => setSuggestionsOpen(true)}
                onBlur={() => setTimeout(() => setSuggestionsOpen(false), 150)}
                placeholder="Szukaj kontrahenta lub NIP…"
                data-testid="invoice-agent-search-input"
                className="w-full h-9 pl-8 pr-8 rounded border border-gray-300 bg-white text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                autoComplete="off"
                spellCheck={false}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => { setSearchTerm(""); setSuggestionsOpen(false); }}
                  data-testid="invoice-agent-search-clear"
                  aria-label="Wyczyść wyszukiwarkę"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              )}

              {/* Banking-style dropdown sugestii (top 5) po ≥3 znakach. */}
              {suggestionsOpen && suggestions.length > 0 && (
                <ul
                  className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
                  data-testid="invoice-agent-search-suggestions"
                >
                  {suggestions.map((s) => (
                    <li key={s.vendor_nip}>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()} // zapobiega blur inputa
                        onClick={() => {
                          setSearchTerm(s.vendor_name || s.vendor_nip || "");
                          setSuggestionsOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-indigo-50 flex items-center justify-between gap-3"
                        data-testid={`invoice-agent-search-suggestion-${s.vendor_nip}`}
                      >
                        <span className="truncate">{s.vendor_name || "(brak nazwy)"}</span>
                        <span className="text-xs text-gray-500 shrink-0 font-mono">
                          {s.vendor_nip}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Sortowanie A-Z / Z-A — jeden przycisk toggle. */}
            <button
              type="button"
              onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              data-testid="invoice-agent-sort-toggle"
              aria-label={sortDir === "asc" ? "Sortuj Z-A" : "Sortuj A-Z"}
              className="h-9 px-3 rounded border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 shrink-0"
              title={sortDir === "asc" ? "Sortuj Z-A" : "Sortuj A-Z"}
            >
              {sortDir === "asc"
                ? <ArrowUpAZ className="w-4 h-4 text-gray-500" />
                : <ArrowDownAZ className="w-4 h-4 text-gray-500" />}
              <span>Sortuj {sortDir === "asc" ? "A-Z" : "Z-A"}</span>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Log ostatniej analizy Agenta Faktur */}
      {lastDebugInfo && (
        <AgentDebugPanel
          title="Log ostatniej analizy Agenta Faktur"
          debugInfo={lastDebugInfo}
          testIdPrefix="invoice-debug"
          summaryRows={[
            {
              label: "NIP dostawcy",
              value: lastDebugInfo.entity_id || lastDebugInfo.vendor_nip || "—",
            },
            {
              label: "Liczba faktur",
              value: lastDebugInfo.input_payload?.invoice_count_sent_to_llm
                ?? lastDebugInfo.aggregation?.invoices_fetched ?? "—",
            },
            {
              label: "Distinct bank_account",
              value: lastDebugInfo.aggregation?.distinct_bank_accounts_count ?? 0,
            },
            {
              label: "known_ibans w profilu",
              value: (() => {
                const inner = lastDebugInfo.saved_profile?.profile || {};
                const ibans = inner.known_ibans || inner.bank_accounts || [];
                return Array.isArray(ibans) ? ibans.length : 0;
              })(),
            },
            {
              label: "Profil bankowy",
              value: lastDebugInfo.aggregation?.bank_profile_source || "—",
            },
            {
              label: "match_method",
              value: lastDebugInfo.aggregation?.bank_profile_match_method || "—",
            },
            {
              label: "Status zapisu",
              value: lastDebugInfo.aggregation?.save_status || "—",
            },
            {
              label: "Recommendation",
              value: lastDebugInfo.saved_profile?.profile?.payment_method || "—",
            },
          ]}
        />
      )}

      {/* Vendor list */}
      <div className="space-y-2 landscape-card-grid" data-testid="invoice-agent-vendor-list">
        {items.map((v, idx) => {
          const prof = v.profile?.profile || null;
          const nip = v.vendor_nip;
          const rowStatus = computeRowStatus(v);
          const isExpanded = expanded.has(nip);
          const isBusy = busyKeys.has(nip);
          const bank = v.bank_agent_profile;

          return (
            <Card
              key={nip}
              className="border border-gray-200"
              data-testid={`invoice-agent-vendor-row-${idx}`}
            >
              <CardContent className="p-3">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  <button
                    type="button"
                    onClick={() => toggleExpand(nip)}
                    className="text-gray-400 hover:text-gray-700 shrink-0 mt-0.5"
                    aria-label={isExpanded ? "Zwiń" : "Rozwiń szczegóły"}
                    data-testid={`invoice-agent-vendor-expand-${idx}`}
                  >
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="font-medium text-gray-900 text-sm break-words"
                        title={v.vendor_name}
                        data-testid={`invoice-agent-vendor-name-${idx}`}
                      >
                        {v.vendor_name || "(brak nazwy)"}
                      </span>
                      <span
                        className="text-xs font-mono text-gray-500"
                        data-testid={`invoice-agent-vendor-nip-${idx}`}
                      >
                        NIP {nip}
                      </span>
                      <StatusBadge status={rowStatus} />
                      <BankConfirmationBadge
                        hasBankProfile={!!v.has_bank_profile}
                        paymentMethod={prof?.payment_method}
                        idx={idx}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>
                        Obroty: <strong className="tabular-nums text-gray-700">{formatPLN(v.total_gross)}</strong>
                      </span>
                      <span>
                        Faktur: <strong className="text-gray-700">{v.invoice_count}</strong>
                        {v.sources?.ksef_inbox > 0 && (
                          <span className="text-indigo-700"> (+{v.sources.ksef_inbox} w KSeF Inboxie)</span>
                        )}
                      </span>
                      <span>
                        Ostatnia: <strong className="text-gray-700">{(v.last_invoice_date || "—").slice(0, 10)}</strong>
                      </span>
                      {(v.currencies || []).length > 1 && (
                        <span className="text-orange-700">Waluty: {v.currencies.join(", ")}</span>
                      )}
                    </div>

                    {/* Profil płatniczy z Agenta Wyciągów (read-only).
                        2026-07: Wyciszony dla metod bez przelewu — jeżeli Agent
                        Faktur ustalił cash/blik/card/online, profil bankowy
                        NIE jest istotny (patrz NON_TRANSFER_METHODS). */}
                    {bank && !NON_TRANSFER_METHODS.has(prof?.payment_method) && (
                      <div
                        className="text-xs mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-50 text-indigo-800 border border-indigo-200"
                        data-testid={`invoice-agent-vendor-bank-${idx}`}
                      >
                        <Banknote className="w-3 h-3" />
                        Agent Wyciągów: <strong>{METHOD_LABEL[bank.payment_method] || bank.payment_method}</strong>
                        {typeof bank.confidence === "number" && (
                          <span className="text-indigo-600">· {(Number(bank.confidence) * 100).toFixed(0)}%</span>
                        )}
                      </div>
                    )}

                    {/* Profil Agenta Faktur */}
                    {prof && (
                      <div
                        className="text-xs mt-1.5 text-emerald-800"
                        data-testid={`invoice-agent-vendor-profile-${idx}`}
                      >
                        Profil faktur: <strong>{METHOD_LABEL[prof.payment_method] || prof.payment_method}</strong>
                        {typeof prof.payment_confidence === "number" && (
                          <span className="text-emerald-600"> · pewność {(Number(prof.payment_confidence) * 100).toFixed(0)}%</span>
                        )}
                        {prof.payment_source && (
                          <span className="text-gray-500"> · źródło: {prof.payment_source}</span>
                        )}
                      </div>
                    )}

                    {prof?.needs_user_review && prof?.review_reason && (
                      <div
                        className="text-xs mt-1.5 text-orange-800"
                        data-testid={`invoice-agent-vendor-review-${idx}`}
                      >
                        Wymaga uwagi: {prof.review_reason}
                      </div>
                    )}
                  </div>

                  {/* „Sprawdź ponownie" — pojedyncza analiza dla tej karty
                      (force=true). Zawsze widoczna. Wyłączona podczas
                      lokalnej analizy lub aktywnego job-a auto-analizy. */}
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={isBusy || autoJob.running}
                    onClick={async () => {
                      setBusyKeys((s) => new Set(s).add(nip));
                      try {
                        // Zbierz invoice_ids — z `invoices/vendors/{nip}/invoices`
                        const r = await axios.get(
                          `${API}/ai/agents/invoice/vendors/${encodeURIComponent(nip)}/invoices`,
                          { withCredentials: true }
                        );
                        const ids = (r.data?.invoices || []).map((i) => i.invoice_id).filter(Boolean);
                        if (!ids.length) {
                          toast.error("Brak faktur do analizy.");
                          return;
                        }
                        const respAnalyze = await axios.post(
                          `${API}/ai/agents/invoice/analyze-vendor`,
                          { vendor_nip: nip, invoice_ids: ids, force: true },
                          { withCredentials: true }
                        );
                        if (respAnalyze?.data?.debug_info) {
                          setLastDebugInfo(respAnalyze.data.debug_info);
                        }
                        // Pakiet A 2026-02: bramka XML — status blokujący.
                        // Backend zwraca status ∈ {skipped_pending_xml,
                        // skipped_legacy_schema, skipped_xml_enrichment_failed}
                        // + stabilny reason_code + liczniki. Nie liczymy
                        // tego jako sukcesu analizy.
                        const rd = respAnalyze?.data || {};
                        const rc = rd.reason_code;
                        if (rc === "pending_xml_enrichment") {
                          const rc_ready = rd.ready_count ?? 0;
                          const rc_total = rd.total ?? 0;
                          toast.warning("Analiza wstrzymana", {
                            description: `Trwa pobieranie pełnych danych faktur z KSeF. Gotowe: ${rc_ready} z ${rc_total}.`,
                          });
                        } else if (rc === "legacy_schema_unsupported") {
                          const lc = rd.legacy_count ?? 0;
                          const msg = lc > 1
                            ? `Wykryto ${lc} faktur w starszym, nieobsługiwanym schemacie KSeF. Nie można ich bezpiecznie uwzględnić w analizie.`
                            : `Wykryto fakturę w starszym, nieobsługiwanym schemacie KSeF. Nie można jej bezpiecznie uwzględnić w analizie.`;
                          toast.error("Niewspierany schemat KSeF", { description: msg });
                        } else if (rc === "xml_enrichment_failed") {
                          const fc = rd.failed_count ?? 0;
                          toast.error("Błąd danych KSeF", {
                            description: `Nie udało się pobrać pełnych danych ${fc} ${fc === 1 ? "faktury" : "faktur"} z KSeF.`,
                          });
                        } else {
                          toast.success("Profil odświeżony.");
                        }
                        await fetchData();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Błąd analizy.");
                      } finally {
                        setBusyKeys((s) => { const ns = new Set(s); ns.delete(nip); return ns; });
                      }
                    }}
                    data-testid={`invoice-agent-vendor-reanalyze-${idx}`}
                    className="shrink-0"
                  >
                    {isBusy
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                    Sprawdź ponownie
                  </Button>
                </div>
              </CardContent>
              {isExpanded && <VendorInvoices vendorNip={nip} />}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default InvoiceAgentTab;
